import { useState } from 'react'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import {
  PerformanceGoalsCreateMetricGroupDocument,
  PerformanceGoalsMetricGroupSelectFieldDocument,
  PerformanceGoalsMetricGroupSelectFieldGroupFragment,
  PerformanceGoalsMetricGroupSelectFieldPolicyDocument,
} from 'generated/resources/graphql'
import { uniqBy } from 'lodash'

import SelectOrCreate from 'modules/core/components/SelectOrCreate'
import Selector from 'modules/core/components/Selector'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useMutation } from 'modules/core/hooks/useMutation'
import { usePaginatedQuery } from 'modules/core/hooks/usePaginatedQuery'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

export const MetricGroupSelectField: React.FC<{
  value?: string
  error?: string
  touched?: boolean
  onChange: (value: string | null) => void
  defaultMetricGroup?: PerformanceGoalsMetricGroupSelectFieldGroupFragment | null
}> = ({ value, onChange, error, defaultMetricGroup, touched }) => {
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [search, setSearch] = useState<string>('')
  const [selected, setSelected] =
    useState<PerformanceGoalsMetricGroupSelectFieldGroupFragment | null>(null)

  const { data: policyData, loading: loadingPolicy } = useQuery(
    PerformanceGoalsMetricGroupSelectFieldPolicyDocument
  )

  const {
    nodes,
    loading,
    controls: { fetchMore, hasMore, loading: loadingMore },
  } = usePaginatedQuery(PerformanceGoalsMetricGroupSelectFieldDocument, {
    variables: { search },
    extractConnection: (data) => data.goals.metricGroupsConnection,
  })

  const [createMetricGroup, { loading: creatingMetricGroup }] = useMutation(
    PerformanceGoalsCreateMetricGroupDocument,
    {
      extractorFn: (data) => data.goalsMutations.createGoalsMetricGroup,
      refetchQueries: [PerformanceGoalsMetricGroupSelectFieldDocument],
      awaitRefetchQueries: true,
    }
  )

  const metricGroups = uniqBy(selected ? [selected, ...nodes] : nodes, (group) => group.id)

  const canCreate = Boolean(policyData?.goals?._policies?.createMetricGroup.can)

  const handleChange = (id: string | string[] | null) => {
    if (!id) {
      onChange(null)
      setSelected(null)
      return
    }

    const stringId = String(id)
    const group = metricGroups.find((g) => g.id === stringId)

    onChange(stringId)
    setSelected(group ?? null)
  }

  const handleGoalCreate = async (name: string) => {
    const response = await createMetricGroup({ variables: { title: name } })
    const result = handleResult(response, () => undefined)

    if (!result.success) return

    const createdGroup = result.data?.metricGroup
    if (!createdGroup) return

    onChange(createdGroup.id)
    setSelected(createdGroup)
    setIsOpen(false)

    app.setFlash({
      type: 'correct',
      message: i18n.t('common.saved_successfully'),
    })
  }

  useUnsafeEffect(() => {
    if (defaultMetricGroup) setSelected(defaultMetricGroup)
  }, [defaultMetricGroup])

  const creationComponent = (name: string) =>
    canCreate && (
      <Box
        alignItems="center"
        justifyContent="spaceBetween"
        flexDirection="row"
        paddingX="s16"
        paddingY="s6"
      >
        <Text.Normal size="200" weight="medium">
          {name}
        </Text.Normal>
        <Button
          type="secondary"
          onClick={() => handleGoalCreate(name)}
          loading={creatingMetricGroup}
        >
          {i18n.t('performance_goals.goal_list.create_button')}
        </Button>
      </Box>
    )

  const placeholder = canCreate
    ? i18n.t('performance_goals.goal_select_field.placeholder_can_create')
    : i18n.t('performance_goals.goal_select_field.placeholder')

  return (
    <SelectOrCreate.Popover
      label={i18n.t('performance_goals.goal_select_field.label')}
      noResultsLabel={
        canCreate
          ? i18n.t('performance_goals.goal_select_field.blank_label_can_create')
          : i18n.t('performance_goals.goal_select_field.blank_label')
      }
      placeholder={placeholder}
      defaultSearch={search}
      loading={loading || loadingMore || loadingPolicy}
      isMultiSelect={false}
      inputStyle="badge"
      error={touched ? error : undefined}
      values={value ? [value] : []}
      creationComponent={creationComponent}
      isOpen={isOpen}
      fetchMore={fetchMore}
      hasMore={hasMore}
      onChange={handleChange}
      onValuesChange={handleChange}
      onToggle={setIsOpen}
      onInputChange={(input) => {
        if (value && input && !isOpen) setIsOpen(true)
        setSearch(input)
      }}
    >
      {metricGroups.map(({ id, title }) => (
        <Selector.Option key={id} value={id} label={title} searchValue={title}>
          {title}
        </Selector.Option>
      ))}
    </SelectOrCreate.Popover>
  )
}
